/**
 * @documental
 * @export Textbox
 */

import { forwardRef } from "react";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Field } from "./Field";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { SharedProps } from "./SharedProps";
import { Input } from "./Input";
import { Placeholder } from "./Placeholder";


export interface TextboxProps extends InputBaseProps<string | null>, SharedProps {}

/**
 * The `Textbox` component sub components.
 */
export interface TextboxSubComponents {
  /**
   * A React Hook Form compatible Textbox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the Textbox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * An input component which allows the user to enter a single line of plain text.
 *
 *  `import { Textbox } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>To use the React Hook Form version of the textbox add the following:</strong>
 *
 * `<Textbox.Field .../>`
 *
 * <strong>To use the regular version of the Textbox</strong>
 *
 * `<Textbox .../>`
 *
 * The regular version acts like a normal input field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to actually be interactive
 *
 * ```typescript
 * const [value, onChange] = useSimpleInputState<string | null | undefined>("");

   const handleChange = (prev: any) => {
      onChange(prev, value);
   }

   return (
     <Textbox
       label="None RHF Textbox"
       placeholder="Sample Text"
       onChange={handleChange} value={value}
       instructions="Sample instructions"
       {...props}
     />
   )
  ```
 */
export const Textbox = withSubComponents<RefComponent<HTMLInputElement, TextboxProps>, TextboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, TextboxProps>(function Textbox(props, ref) {
    return <Input {...props} ref={ref} type="text" />;
  }),
);
